// Asked once, after a scenario card is picked and before the chat opens: who is
// this person to you? A name is required, since it's what the whole chat calls
// them. A description is optional, and colours both the avatar and what the
// character knows about themselves.

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MAX_NAME_CHARS, MAX_DESCRIPTION_CHARS } from '../shared/persona';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 100;
`;

const Dialog = styled.form`
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  padding: 1.75rem;
  width: 100%;
  max-width: 440px;
`;

const Heading = styled.h3`
  margin: 0 0 0.35rem;
  color: #0d9488;
  font-size: 1.15rem;
`;

const Sub = styled.p`
  margin: 0 0 1.25rem;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.45;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 1.1rem;
`;

const FieldName = styled.span`
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  color: #444;
  font-weight: 600;
`;

const Optional = styled.span`
  font-weight: 400;
  color: #999;
`;

const inputStyles = `
  width: 100%;
  padding: 0.7rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0d9488;
  }
`;

const NameInput = styled.input`${inputStyles}`;

const DescriptionInput = styled.textarea`
  ${inputStyles}
  resize: none;
  line-height: 1.4;
`;

const Counter = styled.span`
  display: block;
  margin-top: 0.25rem;
  text-align: right;
  font-size: 0.75rem;
  color: ${({ $near }) => ($near ? '#b45309' : '#bbb')};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.7rem 1.4rem;
  border-radius: 4px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${({ $primary }) => ($primary ? '#0d9488' : '#ddd')};
  background-color: ${({ $primary }) => ($primary ? '#0d9488' : 'white')};
  color: ${({ $primary }) => ($primary ? 'white' : '#555')};

  &:hover:not(:disabled) {
    background-color: ${({ $primary }) => ($primary ? '#0f766e' : '#f5f5f5')};
  }

  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
  }
`;

const CharacterSetup = ({ scenario, onStart, onCancel }) => {
  // Seeded with the scenario's pinned name where it has one ("Mom"), so the
  // common case is to press Start.
  const [name, setName] = useState(scenario.partner.displayName || '');
  const [description, setDescription] = useState('');
  const nameRef = useRef(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const trimmedName = name.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!trimmedName) return;
    onStart({ name: trimmedName, description: description.trim() });
  };

  return (
    <Backdrop onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <Dialog
        onSubmit={handleSubmit}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Set up ${scenario.title}`}
      >
        <Heading>{scenario.title}</Heading>
        <Sub>
          You'll be talking with {scenario.partner.subtitle.toLowerCase()}. What should we call them?
        </Sub>

        <Field>
          <FieldName>Their name</FieldName>
          <NameInput
            ref={nameRef}
            value={name}
            maxLength={MAX_NAME_CHARS}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sarah"
          />
        </Field>

        <Field>
          <FieldName>
            About them <Optional>(optional)</Optional>
          </FieldName>
          <DescriptionInput
            value={description}
            rows={3}
            maxLength={MAX_DESCRIPTION_CHARS}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Anything that would help: what they're like, how they talk, your history with them."
          />
          <Counter $near={description.length > MAX_DESCRIPTION_CHARS - 40}>
            {description.length}/{MAX_DESCRIPTION_CHARS}
          </Counter>
        </Field>

        <Actions>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" $primary disabled={!trimmedName}>
            Start
          </Button>
        </Actions>
      </Dialog>
    </Backdrop>
  );
};

export default CharacterSetup;
